import { CommonModule } from '@angular/common';
import {
  Component,
  effect,
  ElementRef,
  EventEmitter,
  inject,
  Input,
  OnDestroy,
  Output,
  signal,
  ViewChild,
} from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { BlobService } from '@insite-store360/services';
import { catchError, filter, map, of, Subscription, switchMap } from 'rxjs';
import {
  CropperDialogComponent,
  CropperDialogResult,
} from '../cropper-dialog/cropper-dialog.component';

@Component({
  selector: 'lib-user-profile',
  standalone: true,
  imports: [CommonModule, FormsModule, ReactiveFormsModule],
  templateUrl: './user-profile.component.html',
  styleUrl: './user-profile.component.scss',
})
export class UserProfileComponent implements OnDestroy {
  private readonly config = signal<{
    width: number;
    height: number;
  }>({ width: 0, height: 0 });

  /** Readable URL shown in the header. Azure blobs are private, so the stored URL is signed first. */
  public imageSource = '';
  private profileRequest?: Subscription;
  private readonly blobService = inject(BlobService);

  @Input() set profileUrl(value: string | undefined) {
    this.loadProfileImage(value || '');
  }
  @Input() public height?: string;
  @Input() public width?: string;
  @Output() imageReady = new EventEmitter<Blob>();

  @Input({ required: true }) set Cropwidth(val: number) {
    this.config.update((c) => ({ ...c, width: val }));
  }

  @Input({ required: true }) set Cropheight(val: number) {
    this.config.update((c) => ({ ...c, height: val }));
  }

  private readonly croppedImageURL = signal<string | undefined>(undefined);
  private readonly dialog = inject(MatDialog);
  @ViewChild('fileInput')
  private readonly inputField!: ElementRef<HTMLInputElement>;

  constructor() {
    effect(() => {
      const url = this.croppedImageURL();
      if (url) {
        // Convert the URL to blob before emitting
        fetch(url)
          .then((res) => res.blob())
          .then((blob) => this.imageReady.emit(blob));
      }
    });
  }

  ngOnDestroy(): void {
    this.profileRequest?.unsubscribe();
  }

  /**
   * Stored profile URLs point at a private Azure container. Sign them (or stream
   * them through blob-service) so the header avatar can actually render.
   */
  private loadProfileImage(url: string): void {
    this.profileRequest?.unsubscribe();
    if (!url) {
      if (!this.croppedImageURL()) {
        this.imageSource = '';
      }
      return;
    }
    if (!this.blobService.isAzureBlobUrl(url)) {
      this.imageSource = url;
      return;
    }

    this.profileRequest = this.blobService.toReadSasUrlIfBlob(url, 60, true).pipe(
      switchMap((resolved) => {
        const looksSigned = typeof resolved === 'string' && /[?&]sig=/i.test(resolved);
        if (looksSigned) {
          return of(resolved);
        }
        return this.blobService.fetchMediaFile(url).pipe(
          map((file) => (file && file.size > 0 ? URL.createObjectURL(file) : resolved || '')),
          catchError(() => of(resolved || '')),
        );
      }),
      catchError(() =>
        this.blobService.fetchMediaFile(url).pipe(
          map((file) => (file && file.size > 0 ? URL.createObjectURL(file) : '')),
          catchError(() => of('')),
        ),
      ),
    ).subscribe((resolved) => {
      this.imageSource = resolved || '';
    });
  }

  public onImageError(): void {
    this.imageSource = '';
  }

  public fileSelected(event: Event): void {
    const file = (event.target as HTMLInputElement)?.files?.[0];
    if (!file) return;

    const { width, height } = this.config();
    const dialogRef = this.dialog.open(CropperDialogComponent, {
      data: { image: file, width, height },
      width: '500px',
    });

    dialogRef
      .afterClosed()
      .pipe(filter(Boolean))
      .subscribe((result: CropperDialogResult) => {
        this.uploadImage(result.blob);
      });
  }

  public async uploadImage(blob: Blob) {
    const objectUrl = URL.createObjectURL(blob);
    this.croppedImageURL.set(objectUrl);
    this.imageSource = objectUrl;
  }

  public triggerInputClick() {
    this.inputField.nativeElement.click();
  }
}
